import { ClearOutlined, FilterOutlined } from '@ant-design/icons'
import {
  GraviButton,
  Horizontal,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Popover } from 'antd'
import { type ReactNode, useState } from 'react'

const LABEL_STYLE = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase' as const,
}

const CLEAR_STYLE = { color: 'hsl(var(--primary))' }

/* GraviButton maps appearance='outline' onto antd's `filled` variant, whose
   tint all but disappears against a grid control bar. Borrow the border from
   the Prod/Test/All segmented control it sits beside so the two read as a
   matched pair of controls rather than as loose text. */
const TRIGGER_STYLE = { border: '1px solid hsl(var(--border))' }

/* The trigger pair keeps this width whether or not the clear button is
   showing, so setting or clearing a filter doesn't shuffle everything else
   along the control bar. Wide enough for a two-digit count. */
const TRIGGER_WIDTH = 140

const FILTER_BUTTON_STYLE = { flex: 1, minWidth: 0 }
const FILTER_BUTTON_OUTLINE_STYLE = { ...FILTER_BUTTON_STYLE, ...TRIGGER_STYLE }

const DIVIDER_STYLE = { borderTop: '1px solid var(--border)' }

/** Heading above one group of controls inside a FiltersPopover. */
export function SectionLabel({ children }: { children: string }) {
  return (
    <Texto appearance='hint' style={LABEL_STYLE}>
      {children}
    </Texto>
  )
}

/**
 * A grid control bar's Filters button and the popover behind it: the caller
 * supplies the sections, this supplies the button, the spacing and the Clear
 * row at the foot.
 */
export function FiltersPopover({
  width,
  activeCount = 0,
  onClear,
  open,
  onOpenChange,
  children,
}: {
  width?: number
  /** How many filters are set. Anything above zero turns the button solid and
   * gives it its own clear control, so a collapsed popover can't hide the fact
   * that the grid is narrowed. */
  activeCount?: number
  onClear: () => void
  /** Pass both to drive the popover from the caller -- a popover whose
   * sections edit a draft needs to know when it closes, so it can apply
   * what was edited. Omit both to let it manage its own open state. */
  open?: boolean
  onOpenChange?: (open: boolean) => void
  children: ReactNode
}) {
  const isFiltered = activeCount > 0

  // Open state is the caller's when they pass it and ours otherwise, so Save
  // has something to close either way. Routing Save through the same
  // onOpenChange as a click-away means a caller that applies a draft on close
  // applies it here too -- Save really does save, it just isn't this
  // component's business how.
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isOpen = open ?? uncontrolledOpen
  const handleOpenChange = (next: boolean) => {
    if (open === undefined) setUncontrolledOpen(next)
    onOpenChange?.(next)
  }

  return (
    <Horizontal gap={4} width={TRIGGER_WIDTH}>
      <Popover
        content={
          <Vertical gap={16} style={{ width }}>
            {children}
            <div style={DIVIDER_STYLE} />
            <Horizontal justifyContent='space-between' alignItems='center'>
              <GraviButton
                appearance='text'
                size='small'
                style={CLEAR_STYLE}
                onClick={onClear}
              >
                Clear
              </GraviButton>
              {/* Filters apply as they are picked, so this only closes the
                  popover -- but without it people sit here looking for the
                  button that commits their selection. */}
              <GraviButton
                theme1
                size='small'
                onClick={() => handleOpenChange(false)}
              >
                Save
              </GraviButton>
            </Horizontal>
          </Vertical>
        }
        trigger='click'
        placement='bottomRight'
        open={isOpen}
        onOpenChange={handleOpenChange}
        getPopupContainer={() => document.body}
      >
        {/* Default size rather than small, to stand the same height as the
            segmented control beside it. Once filters are set it goes solid
            primary -- `theme1` is excalibrr's info-coloured button, which is
            the same purple the rest of the control bar accents with, and
            brings its own hover and active states. */}
        <GraviButton
          appearance={isFiltered ? undefined : 'outline'}
          theme1={isFiltered}
          icon={<FilterOutlined />}
          style={isFiltered ? FILTER_BUTTON_STYLE : FILTER_BUTTON_OUTLINE_STYLE}
        >
          {isFiltered ? `Filters (${activeCount})` : 'Filters'}
        </GraviButton>
      </Popover>
      {/* A button of its own rather than an icon inside the one above: sharing
          the trigger meant the only thing marking it clickable was the icon
          itself, and every click had to be kept from opening the popover. */}
      {isFiltered && (
        <GraviButton
          theme1
          icon={<ClearOutlined />}
          title='Clear filters'
          aria-label='Clear filters'
          onClick={onClear}
        />
      )}
    </Horizontal>
  )
}
